import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { PackingCase, GearItemWithPacking } from "@/types/packing";

export function usePackingCases(projectId: string) {
  const queryClient = useQueryClient();

  const { data: cases, isLoading } = useQuery({
    queryKey: ["packing-cases", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("packing_cases")
        .select("*")
        .eq("project_id", projectId)
        .order("sort_order", { ascending: true });

      if (error) throw error;
      return data as PackingCase[];
    },
    enabled: !!projectId,
  });

  const createCase = useMutation({
    mutationFn: async (data: { name: string; description?: string; color?: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const { data: newCase, error } = await supabase
        .from("packing_cases")
        .insert({
          project_id: projectId,
          name: data.name,
          description: data.description,
          color: data.color || '#3b82f6',
          created_by: user.id,
        })
        .select()
        .single();

      if (error) throw error;
      return newCase;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["packing-cases", projectId] });
      toast.success("Case created successfully");
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to create case");
    },
  });

  const updateCase = useMutation({
    mutationFn: async ({ caseId, updates }: { caseId: string; updates: Partial<PackingCase> }) => {
      const { error } = await supabase
        .from("packing_cases")
        .update(updates)
        .eq("id", caseId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["packing-cases", projectId] });
      toast.success("Case updated successfully");
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to update case");
    },
  });

  const deleteCase = useMutation({
    mutationFn: async (caseId: string) => {
      // First, get the case name to unassign items
      const { data: caseData } = await supabase
        .from("packing_cases")
        .select("name")
        .eq("id", caseId)
        .single();

      if (caseData) {
        // Unassign all items from this case (set assigned_case to null)
        await supabase
          .from("gear_items")
          .update({ assigned_case: null })
          .eq("assigned_case", caseData.name);
      }

      // Then delete the case
      const { error } = await supabase
        .from("packing_cases")
        .delete()
        .eq("id", caseId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["packing-cases", projectId] });
      queryClient.invalidateQueries({ queryKey: ["gear-items-packing", projectId] });
      toast.success("Case deleted and items moved back to gear list");
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to delete case");
    },
  });

  return {
    cases,
    isLoading,
    createCase,
    updateCase,
    deleteCase,
  };
}

export function useGearItemsPacking(projectId: string, sortBy: 'category' | 'vendor' | 'name' = 'category') {
  const queryClient = useQueryClient();

  const { data: gearItems, isLoading } = useQuery({
    queryKey: ["gear-items-packing", projectId, sortBy],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("gear_items")
        .select("*, vendors(name)")
        .eq("project_id", projectId);

      if (error) throw error;
      
      const mappedData = data.map(item => ({
        ...item,
        vendor_name: item.vendors?.name,
      })) as GearItemWithPacking[];

      // Sort the data based on the sortBy parameter
      const sortedData = mappedData.sort((a, b) => {
        switch (sortBy) {
          case 'vendor':
            const vendorA = a.vendor_name || '';
            const vendorB = b.vendor_name || '';
            return vendorA.localeCompare(vendorB);
          case 'name':
            return a.name.localeCompare(b.name);
          default:
            return a.category.localeCompare(b.category);
        }
      });

      return sortedData;
    },
    enabled: !!projectId,
  });

  const togglePacked = useMutation({
    mutationFn: async ({ itemId, isPacked }: { itemId: string; isPacked: boolean }) => {
      const { error } = await supabase
        .from("gear_items")
        .update({ is_packed: isPacked })
        .eq("id", itemId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["gear-items-packing", projectId] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to update packing status");
    },
  });

  const assignToCase = useMutation({
    mutationFn: async ({ itemId, caseName }: { itemId: string; caseName: string | null }) => {
      const { error } = await supabase
        .from("gear_items")
        .update({ assigned_case: caseName })
        .eq("id", itemId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["gear-items-packing", projectId] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to assign item to case");
    },
  });

  return {
    gearItems,
    isLoading,
    togglePacked,
    assignToCase,
  };
}